import { NextPage } from 'next';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import * as React from 'react';

import { IMAGES, SITE_URL } from '@/lib/constants';
import { getSortedPostsData, PostData } from '@/lib/posts';

import PostCard from '@/components/blog/PostCard';
import Layout from '@/components/layout/Layout';

type BlogProps = {
  allPostsData: PostData[];
};

export async function getStaticProps() {
  const allPostsData = getSortedPostsData();
  return {
    props: {
      allPostsData,
    },
  };
}

const TITLE = `Blog: Tips for making clips and creator success`;
const DESC = `The next generation AI tool that makes ready-to-post clips from your videos in seconds.`;

const Blog: NextPage<BlogProps> = ({ allPostsData }) => {
  const pinnedPost = allPostsData.filter((post) => post.pinned === true)[0];

  return (
    <>
      <NextSeo
        title={TITLE}
        description={DESC}
        openGraph={{
          url: `${SITE_URL}/blog`,
          title: TITLE,
          description: DESC,
          images: [
            {
              url: IMAGES.BLOG_COVER,
              width: 1200,
              height: 628,
              type: 'image/jpeg',
            },
          ],
        }}
      />
      <Layout mode='light'>
        <section className='bg-gray-100'>
          <div className='flex flex-col items-center mx-auto max-w-[1200px] pt-12 pb-20'>
            <div className="text-center">
              <h1 className="text-center mb-2 font-bold text-5xl">Creator Resources</h1>
              <p className="text-lg text-gray-700">Tips and strategies to elevate your content creation process.</p>
            </div>
          </div>
        </section>
        <section className='flex flex-col mx-auto max-w-[1200px] mt-12 pb-20'>
          <div className='space-y-6'>
            {pinnedPost && (
              <div className='mb-[100px] md:mb-[150px]'>
                <div className='grid md:grid-cols-2 md:pl-10'>
                  <div className='flex flex-col items-start justify-center gap-10'>
                    <Link href={`/blog/${pinnedPost.id}/`}>
                      <h2>{pinnedPost.title}</h2>
                    </Link>
                    <p className='max-w-md text-3xl font-thin text-gray-900'>
                      {pinnedPost.subtitle}
                    </p>
                    <p className='text-gray-800'>
                      {pinnedPost.author} • {pinnedPost.date}
                    </p>
                  </div>
                  <div>
                    <img
                      className='aspect-[9/6] w-full rounded object-cover'
                      src={pinnedPost.coverImage}
                      alt={pinnedPost.title}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className='grid md:grid-cols-3'>
              {allPostsData
                .filter((post) => post.pinned !== true)
                .map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Blog;
